import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type TabValue = 'people' | 'documents' | 'onboarding' | 'clients-organisation';

interface NavigationState {
  isFromDocument?: boolean;
  previousTab?: TabValue;
}

interface TabContextType {
  activeTab: TabValue;
  setActiveTab: (newValue: TabValue, navigationState?: NavigationState) => void;
  navigationState: NavigationState;
}

const TabContext = createContext<TabContextType | undefined>(undefined);

export const TabProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const pathTab = location.pathname.split('/').pop() as TabValue;
    return pathTab || 'people';
  });

  const [navigationState, setNavigationState] = useState<NavigationState>({});

  const handleTabChange = useCallback(
    (newValue: TabValue, navState?: NavigationState) => {
      // Store the previous tab for reference
      const updatedNavState: NavigationState = {
        ...navState,
        previousTab: activeTab
      };

      setNavigationState(updatedNavState);
      setActiveTab(newValue);

      if (newValue === 'clients-organisation') {
        navigate(`/ndis-operations/corporate-services`, {
          state: updatedNavState
        });
      } else {
        navigate(`/payroll/${newValue}`, {
          state: updatedNavState
        });
      }
    },
    [navigate, activeTab]
  );

  const value = useMemo(
    () => ({ activeTab, setActiveTab: handleTabChange, navigationState }),
    [activeTab, handleTabChange, navigationState]
  );

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
};

export const useTabManagement = () => {
  const context = useContext(TabContext);
  if (!context) throw new Error('useTabManagement must be used within a TabProvider');
  return context;
};
